// eslint-disable-next-line @typescript-eslint/ban-ts-comment
import { lazy, ComponentType, ElementType, Suspense } from 'react';
import * as React from 'react';
import parse, {
  HTMLReactParserOptions,
  Element,
  domToReact,
} from 'html-react-parser';
import { Box } from 'theme-ui';
import { cloneDeep, isFunction, uniqueId } from 'lodash-es';
import AttributeParsers from './AttributeParsers';
import PathShortcuts from './PathShortcuts';
import AttributeFilters from './AttributeFilters';
import AttributeReplacers from './AttributeReplacers';
import ComplexReplacements from './ComplexReplacements';
import TagReplacers from './TagReplacers';
import AttributeValueParsers from './AttributeValueParsers';
import { isMessage, processCommand } from './Commands';
import CommentsParsers, { ICommentReplacerData } from './CommentsParsers';
import contextMethods from './contextMethods';

const parameters = {
  performance: {
    measureTime: false,
  },
};

console.time('Templater parse');
console.time('Templater pre screen unlock');
let templaterTimer: number;

export function importTemplaterComponent(path: string) {
  return lazy<ComponentType<unknown>>(() => {
    return new Promise<{
      default: ComponentType<unknown>;
    }>((resolve) => {
      import(
        /* webpackChunkName: "[request]" */
        /* webpackInclude: /\.tsx?$/ */
        /* webpackPreload: true */
        /* webpackExclude: /projectAnalysisTree/ */
        `/src/dynamic/${path}`
      )
        .then((result: unknown) => {
          resolve(result as any);
        })
        .catch((error: unknown) => {
          resolve({
            default: () => {
              console.error(error);
              throw new Error(
                `The above error ocurred at component ${path}, does it exist?`,
              );
            },
          });
        });
    });
  });
}

export function parseShortcut(shortcut: string) {
  if ('^($)[.'.indexOf(shortcut) !== -1) return `\\${shortcut}`;
  return shortcut;
}
const parsedShortcuts: string[][] = Object.entries(PathShortcuts).map(
  ([shortcut, path]) => {
    return [parseShortcut(shortcut), path];
  },
);

const Wrapper = ({
  children,
  onMount,
}: {
  children: React.ReactNode;
  onMount?: () => void;
}) => {
  React.useEffect(() => {
    if (onMount) {
      onMount();
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return children;
};

export interface IAlterDefaultOptions {
  /**
   * It's possible to replace some attributes and parse the value with
   * a specific replacement function you provide, in order to provide
   * custom behavior to the template.
   *
   * @example
   *
   * attributeParsers: [
   *  {
   *    match: /^data-say-([\w]+)$/,
   *    parse: (matchResult, originalValue) => ({ newName: 'onLoad', newValue:
   *    () => alert(matchResult[1]) })
   *  }
   * ]
   */
  attributeParsers?: typeof AttributeParsers;
  /**
   * It's possible to replace some attributes with this property. Each
   * attribute will be searched in the dictionary and if it's found,
   * then it will be replaced with the value which pairs that key.
   *
   * @example
   *
   * attributesReplacement: {
   *  class: 'className' // Very common in react, it's implemented by default
   * }
   */
  attributesReplacement?: Record<string, string>;
  /**
   * With this parameter the templater shouts to the console the important
   * events it goes through.
   */
  debug?: boolean;
  /**
   * It's possible to filter some attributes passing an array of undesired
   * values
   *
   * @example
   *
   * filterAttributes: ['data-dangerous']
   */
  filterAttributes?: string[];
  /**
   * WARNING: If
   *
   * The props object can be accessed from the template, in this way,
   * it's possible to pass any kind of javascript data to be used
   * when generating the template's structure.
   */
  props?: Record<string, unknown>;
}

export interface IDownloadAndParseTemplateByName extends IAlterDefaultOptions {
  avoidCache?: boolean;
  /**
   * The request parameters is an object that will be sent as queryString
   * to the server.
   */
  requestParameters: Record<string, unknown>;
}

export type TTemplateParser = (document: Document) => void;

declare global {
  interface Window {
    onPreParseTemplate: TTemplateParser;
  }
}

export const Command = Error;

function makeEmptyCommentsData(): ICommentReplacerData {
  return { customShortcuts: [], SuspenseComponent: () => null };
}

export const parseOptions = (
  customOptions?: IAlterDefaultOptions,
  commentsData?: ICommentReplacerData,
): HTMLReactParserOptions => {
  let actualCommentsData = commentsData ?? makeEmptyCommentsData();

  return {
    trim: true,
    replace: (domNode) => {
      if (customOptions?.debug) console.log(domNode);
      if (domNode.type.toLowerCase() === 'comment') {
        CommentsParsers.forEach((parser) => {
          const matchResult = ((domNode as any).data as string).match(
            parser.match,
          );
          if (matchResult) {
            actualCommentsData = parser.parse(matchResult, actualCommentsData);
          }
        });
      }
      if (domNode instanceof Element && domNode.attribs) {
        let componentName = domNode.attribs['data-component'];

        if (componentName) {
          actualCommentsData.customShortcuts.forEach(
            ([shortcut, replacement]) => {
              if (componentName.match(new RegExp(`^${shortcut}.+`))) {
                componentName =
                  replacement + componentName.slice(shortcut.length);
              }
            },
          );
          parsedShortcuts.forEach(([shortcut, replacement]) => {
            if (componentName.match(new RegExp(`^${shortcut}.+`))) {
              componentName =
                replacement + componentName.slice(shortcut.length);
            }
          });

          if (componentName.startsWith('@'))
            componentName = componentName.slice(1);
        }

        try {
          const Component = componentName
            ? importTemplaterComponent(componentName)
            : (TagReplacers[domNode.name as keyof typeof TagReplacers] ??
              ((props: unknown) => (
                <Box as={domNode.name as ElementType} {...(props as any)} />
              )));

          for (let i = 0; i < ComplexReplacements.length; i++) {
            const complexReplacement = ComplexReplacements[i];
            const result = complexReplacement(
              domNode,
              Component,
              customOptions,
              commentsData,
            );
            if (result !== false) return <Suspense>{result}</Suspense>;
          }

          [
            ...AttributeFilters,
            ...(customOptions?.filterAttributes ?? []),
          ].forEach((att) => {
            // eslint-disable-next-line no-param-reassign
            delete domNode.attribs[att];
          });

          const props = Object.entries(domNode.attribs).reduce(
            (dictionary, [initialName, initialValue]) => {
              const name = initialName;
              const value = initialValue;
              let hasParsed = false;
              const returnDictionary = { ...dictionary };
              for (let i = 0; i < AttributeParsers.length; i++) {
                const parser = AttributeParsers[i];
                const match = name.match(parser.match);
                if (customOptions?.debug) console.log(parser.match, match);
                if (match) {
                  const parsed = parser.parse(match, value);
                  if (customOptions?.debug) console.log(parser.match, parsed);
                  if (parsed) {
                    parsed.forEach(({ newName, newValue }) => {
                      const finalName =
                        {
                          ...AttributeReplacers,
                          ...(customOptions?.attributesReplacement ?? {}),
                        }[newName] ?? newName;
                      returnDictionary[finalName] =
                        AttributeValueParsers(newValue);
                    });
                    hasParsed = true;
                  }
                  break;
                }
              }

              if (!hasParsed) {
                const newName =
                  {
                    ...AttributeReplacers,
                    ...(customOptions?.attributesReplacement ?? {}),
                  }[name] ?? name;
                returnDictionary[newName] = AttributeValueParsers(value);
              }

              return returnDictionary;
            },
            {} as Record<string, unknown>,
          );

          const children: React.ReactNode = domToReact(
            cloneDeep(domNode.children),
            parseOptions(customOptions, actualCommentsData),
          );

          delete props.children;

          const { onComponentload } = props as { onComponentload?: () => void };
          if (onComponentload) {
            delete props.onComponentload;
          }

          if (parameters.performance.measureTime) {
            clearTimeout(templaterTimer);
            templaterTimer = setTimeout(() => {
              console.timeEnd('Templater parse');
            }, 150) as any;
          }

          if ((actualCommentsData as any).SuspenseComponent) {
            if (onComponentload) {
              return (
                <Suspense>
                  <Wrapper onMount={onComponentload}>
                    <Component {...props}>
                      {domNode.children.length > 0 ? children : null}
                    </Component>
                  </Wrapper>
                </Suspense>
              );
            }
            return (
              <Suspense>
                <Component {...props}>
                  {domNode.children.length > 0 ? children : null}
                </Component>
              </Suspense>
            );
          }
          if (onComponentload)
            return (
              <Wrapper onMount={onComponentload}>
                <Component {...props}>
                  {domNode.children.length > 0 ? children : null}
                </Component>
              </Wrapper>
            );
          return (
            <Component {...props}>
              {domNode.children.length > 0 ? children : null}
            </Component>
          );
        } catch (e) {
          if (isMessage(e)) {
            const match = e.message.match(/COMMAND: *(\w[\w\d_-]*)/);
            if (match) {
              return processCommand(match[1]);
            }
          }
          console.error(e);
        }
      }
      return domNode as any;
    },
    htmlparser2: { lowerCaseAttributeNames: false },
  };
};

class Templater {
  private static _instance: Templater | null = null;
  public static get instance() {
    if (!this._instance) {
      this._instance = new Templater();
    }
    return this._instance!;
  }

  isDownloadingTemplate = false;

  templates: Record<string, string> = {};

  private constructor() {
    this.initTemplates();
  }

  private initTemplates() {
    const templates = [
      ...document.querySelectorAll('[data-template]'),
    ] as HTMLElement[];
    const templatesObject = templates.reduce((dictionary, template) => {
      const name = template.getAttribute('data-template');
      if (!name) return dictionary;
      return {
        ...dictionary,
        [name]: this.parseContent(name, template.outerHTML, {}),
      };
    }, {});
    templates.forEach((template) => template.remove());
    this.templates = templatesObject;
  }

  /**
   * This method parses the string looking for <script class="context"> tags
   * and placeholders, run that code and make the replacements in the template
   * string.
   *
   * @param content The HTML string
   */
  private parseContent(
    templateName: string,
    content: string,
    props: Record<string, unknown>,
  ) {
    function shoutError(error: string) {
      console.error('%cParse template error', 'font-size: 16px; margin: 10px');
      throw new Error(`${error} at template ${templateName}`);
    }
    let context: Record<string, string> = {};
    let newContent = content
      .replaceAll(
        /data-json-([\w_-]+)="([^"]+)"/g,
        (_, propName: string, propValue: string) => {
          return `data-json-${propName}='${propValue.replaceAll(
            "'",
            '&apos;',
          )}'`;
        },
      )
      .replaceAll(
        /<script [^>]*class="context"[^>]*>([\s\S]*?)<\/script>/g,
        (_, scriptContent) => {
          if ((scriptContent as string).match(/window[^=;]*=/))
            throw new Error('Assignment to window is forbidden');

          try {
            context = {
              ...context,
              ...(eval(`{
              let context = JSON.parse(\`${JSON.stringify(context).replaceAll(
                '"',
                '\\"',
              )}\`);
              ${scriptContent as string};
              context
            }`) as Record<string, string>),
            };
          } catch (e) {
            console.info(`Script contents: ${scriptContent as string}`);
            console.info(`Props`, props);
            shoutError(e as any);
          }
          return '';
        },
      );

    const contextId = uniqueId();

    const replacementVars = { context, props };
    Object.entries(replacementVars).forEach(([varName, varContent]) => {
      if (varContent)
        Object.entries(varContent).forEach(([key, value]) => {
          if (isFunction(value)) {
            if (!contextMethods[contextId]) contextMethods[contextId] = {};
            const methodId = uniqueId();
            contextMethods[contextId][methodId] = value;
            newContent = newContent.replaceAll(
              new RegExp(`{{\\s*(${varName})\\??.${key}\\s*}}`, 'g'),
              `__contextMethod.${contextId}.${methodId}`,
            );
          } else
            newContent = newContent.replaceAll(
              new RegExp(`{{\\s*(${varName})\\??.${key}\\s*}}`, 'g'),
              value !== undefined ? (value as string) : '',
            );
        });
    });

    newContent = newContent.replaceAll(
      /{{\s*(context|props)\?.([_$a-zA-Z]\w+)\s*}}/g,
      '',
    );

    const result = newContent.match(
      /\{\{(context|props)\.([_$a-zA-Z]\w+)\\s*}}/,
    );

    if (result) shoutError(`${result[1]}.${result[2]} is undefined`);

    return newContent;
  }

  /**
   * This method takes a template from the HTML Document and parses it to React
   * Components.
   *
   * @param name The name of the template you are looking for, it must be specified as
   * data-template="name" in the HTML element you want to use as template.
   * @param options This argument allows you to alter the behavior of the parser, for example
   * to replace some attribute names, parse some attributes with a custom parser function or
   * discard some attributes. See the type IAlterDefaultOptions to get more information
   *
   * @returns A ReactComponent as the result of the parsed HTML template.
   */
  getTemplateByName(name: string, options?: IAlterDefaultOptions) {
    if (!this.templates[name])
      throw new Error('There is no template with such name, ', name as any);
    const component = this.#actuallyParse(
      this.parseContent(name, this.templates[name], options?.props as any),
      parseOptions(options),
    );
    return component;
  }

  /**
   * This method parses a string in HTML to React Components.
   *
   * @param content The HTML string to parse to React Components
   * @param options This argument allows you to alter the behavior of the parser, for example
   * to replace some attribute names, parse some attributes with a custom parser function or
   * discard some attributes. See the type IAlterDefaultOptions to get more information
   *
   * @returns A ReactComponent as the result of the parsed HTML template.
   */
  parseString(content: string, options?: IAlterDefaultOptions) {
    return this.#actuallyParse(
      this.parseContent('parseStringTemplate', content, options?.props as any),
      parseOptions(options),
    );
  }

  #actuallyParse = (
    html: string,
    options?: HTMLReactParserOptions,
  ): string | JSX.Element | JSX.Element[] => {
    let actualHTML = html;

    if (window.onPreParseTemplate) {
      const parser = new DOMParser();
      const document = parser.parseFromString(html, 'text/html');

      window.onPreParseTemplate?.(document);

      actualHTML = document.body.innerHTML;
    }

    return parse(actualHTML, options);
  };
}

export { Templater };
